import { createBaseQuery } from "@/redux/config";
import { createApi } from "@reduxjs/toolkit/query/react";
import { CustomerService } from "./customer-service";

export enum CustomerStatus {
  ACTIVE = "active",
  BLOCKED = "blocked",
  DISABLED = "disabled",
}

export const customerApi = createApi({
  reducerPath: "customerApi",
  baseQuery: createBaseQuery(""),
  tagTypes: [
    "Customer",
    "CustomerAccount",
    "CustomerAccountBalance",
    "CustomerDevice",
    "CurrentCustomer",
    "CustomerReport",
  ],
  endpoints: (build) => ({
    addCustomerAccount: build.mutation<
      ServiceCustomerAccount,
      { params: CustomerSystemRefParams; data: CustomerAccountData }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/accounts/${params.customerSystemRef}`
        ),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CustomerAccount"],
    }),

    editCustomerAccount: build.mutation<
      ServiceCustomerAccount,
      { params: BaseCustomerParams; data: CustomerAccountData }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params, "/accounts"),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["CustomerAccount"],
    }),

    listCustomerAccounts: build.query<
      ServiceCustomerAccount[],
      CustomerSystemRefParams
    >({
      query: (params) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/accounts/${params.customerSystemRef}`
        ),
        method: "GET",
      }),
      providesTags: ["CustomerAccount"],
    }),

    getCustomerAccountBalance: build.query<
      AccountBalanceResponse,
      { params: BaseCustomerParams; accountNo: string }
    >({
      query: ({ params, accountNo }) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/accounts/balance/${accountNo}`
        ),
        method: "GET",
      }),
      providesTags: ["CustomerAccountBalance"],
    }),

    initiateFund: build.mutation<
      AccountBalanceResponse,
      { params: BaseCustomerParams; data: FundAccountReq }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params, `/accounts/init-fund`),
        method: "POST",
        body: data,
      }),
    }),

    authorizeAccount: build.mutation<
      AccountBalanceResponse,
      { params: BaseCustomerParams; data: FundAccountReq }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params, `/accounts/authorize-fund`),
        method: "POST",
        body: data,
      }),
    }),

    addCustomerDevice: build.mutation<
      ServiceCustomerDevice,
      { params: CustomerSystemRefParams; data: CustomerDeviceData }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params, "/devices"),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["CustomerDevice"],
    }),

    editCustomerDevice: build.mutation<
      ServiceCustomerDevice,
      { params: BaseCustomerParams; data: CustomerDeviceData }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params, "/devices"),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["CustomerDevice"],
    }),

    listCustomerDevices: build.query<
      ServiceCustomerDevice[],
      CustomerSystemRefParams
    >({
      query: (params) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/devices/${params.customerSystemRef}`
        ),
        method: "GET",
      }),
      providesTags: ["CustomerDevice"],
    }),

    addCustomer: build.mutation<
      ServiceCustomer,
      { params: BaseCustomerParams; data: CustomerData }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(params),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Customer", "CurrentCustomer"],
    }),

    editCustomer: build.mutation<
      ServiceCustomer,
      {
        params: BaseCustomerParams & { customerSystemRef: string };
        data: CustomerData;
      }
    >({
      query: ({ params, data }) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/${params.customerSystemRef}`
        ),
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Customer", "CurrentCustomer"],
    }),

    getCustomerById: build.query<
      ServiceCustomer,
      {
        params: BaseCustomerParams & { customerSystemRef: any };
      }
    >({
      query: ({ params }) => ({
        url: CustomerService.getEndpointUrl(
          params,
          `/${params.customerSystemRef}`
        ),
        method: "GET",
      }),
      providesTags: ["CurrentCustomer"],
    }),

    listCustomers: build.query<
      ServiceCustomer[],
      { params: BaseCustomerParams; filter: CustomerFilter }
    >({
      query: ({ params, filter }) => ({
        url: CustomerService.getEndpointUrl(params, "/all"),
        method: "POST",
        body: filter,
      }),
      providesTags: ["Customer"],
    }),

    customerAggReport: build.query<
      CustomerAggResult[],
      { params: BaseCustomerParams; filter: CustomerAggFilter }
    >({
      query: ({ params, filter }) => ({
        url: CustomerService.getEndpointUrl(params, "/agg-report"),
        method: "POST",
        body: filter,
      }),
      providesTags: ["Customer"],
    }),
  }),
});
